/**
 * Settings → Plugins: installed packages, the way to Browse, and the app
 * Connections those packages (and everything else) rely on.
 */
import { CONCEPT_ICONS, PocketRow, PocketSection } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { settingsPluginsMessages } from "@/i18n/messages/settings-plugins";
import { attentionStatus, useAppAttentionCount } from "./connections-state";
import { usePluginInstallations } from "./plugins-state";
import { SettingsLoadStatus } from "./settings-load-status";
import { useSettingsNav } from "./settings-navigation";

export function PluginsPage() {
  const t = useT(settingsPluginsMessages);
  const nav = useSettingsNav();
  const installations = usePluginInstallations();
  const appAttention = useAppAttentionCount();
  const settled = !installations.loading && !installations.error;
  return (
    <div data-testid="settings-page-plugins">
      <PocketSection title={t("installed")} testId="plugins-installed">
        <SettingsLoadStatus
          state={installations}
          noun={t("installedPluginsNoun")}
          retryTestId="plugins-retry"
        />
        {settled && !installations.data.length && (
          <p
            className="px-2 py-1 text-appkit-small text-muted"
            data-testid="plugins-installed-empty"
          >
            {t("noPluginsInstalled")}
          </p>
        )}
        {installations.data.map((item) => (
          <PocketRow
            key={item.slug}
            testId={`plugins-installed-${item.slug}`}
            leading={<Icons.Package className="h-4 w-4 text-muted" />}
            title={item.name}
            secondLine={
              item.upgrade_available
                ? t("versionUpdateAvailable", { version: item.version })
                : item.description
            }
            chevron
            onSelect={() =>
              nav.push({
                page: "plugins-plugin",
                args: {
                  slug: item.slug,
                  // A pinned version that is no longer bundled opens the latest one.
                  version: item.available
                    ? item.version
                    : (item.latest_version ?? item.version),
                  title: item.name,
                },
              })
            }
          />
        ))}
      </PocketSection>
      <PocketSection>
        <PocketRow
          testId="plugins-browse"
          title={t("browsePlugins")}
          leading={<Icons.Package className="h-4 w-4 text-muted" />}
          chevron
          onSelect={() => nav.push({ page: "plugins-browse" })}
        />
        <PocketRow
          testId="plugins-connections"
          title={t("connections")}
          leading={<CONCEPT_ICONS.app className="h-4 w-4 text-muted" />}
          status={attentionStatus(appAttention)}
          chevron
          onSelect={() => nav.push({ page: "connections" })}
        />
      </PocketSection>
    </div>
  );
}
